import React, { useState, useEffect } from 'react';
import {
    Stack,
    Text,
    TextField,
    PrimaryButton,
    DefaultButton,
    IconButton,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    Separator,
    Label,
    IStackTokens,
    mergeStyleSets,
    FontWeights,
    Dialog,
    DialogType,
    DialogFooter,
    Checkbox,
    ChoiceGroup,
    IChoiceGroupOption,
    DetailsList,
    IColumn,
    SelectionMode,
    Panel,
    PanelType
} from '@fluentui/react';
import {
    AddRegular,
    DeleteRegular,
    EditRegular,
    CopyRegular,
    BrainCircuitRegular,
    SettingsRegular,
    CheckmarkCircleRegular,
    WarningRegular,
    ArrowClockwiseRegular,
    BeakerRegular,
    ArrowSyncRegular
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';

// Brand Icons for AI Providers
const AzureIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M33.338 6.544h28.098l-29.875 84.706c-0.655 1.857-2.4 3.098-4.358 3.098H6.543c-2.532 0-4.185-2.606-3.132-4.938l28.929-82.866h0.998z" fill="url(#azure-a)"/>
        <path d="M71.175 65.176H30.451c-1.157 0-1.848 1.283-1.211 2.244l24.155 34.428c0.515 0.733 1.356 1.168 2.252 1.168h29.787l-14.259-37.84z" fill="#0078D4"/>
        <path d="M33.338 6.544c-1.944 0-3.679 1.24-4.32 3.076L0.068 90.06c-0.845 2.343 0.846 4.956 3.302 4.956h22.436c1.774-0.171 3.275-1.391 3.902-3.098l5.989-16.998 21.089 18.496c0.515 0.733 1.356 1.168 2.252 1.168h29.787L71.175 52.744H38.024L66.32 6.544H33.338z" fill="url(#azure-b)"/>
        <defs>
            <linearGradient id="azure-a" x1="46.387" y1="13.458" x2="21.867" y2="97.262" gradientUnits="userSpaceOnUse">
                <stop stopColor="#114A8B"/><stop offset="1" stopColor="#0669BC"/>
            </linearGradient>
            <linearGradient id="azure-b" x1="54.919" y1="49.375" x2="47.787" y2="52.188" gradientUnits="userSpaceOnUse">
                <stop stopOpacity="0.3"/><stop offset="0.071" stopOpacity="0.2"/><stop offset="0.321" stopOpacity="0.1"/><stop offset="0.623" stopOpacity="0.05"/><stop offset="1" stopOpacity="0"/>
            </linearGradient>
        </defs>
    </svg>
);

const GeminiIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 24C12 22.0667 11.6333 20.2583 10.9 18.575C10.1833 16.875 9.20833 15.3917 7.975 14.125C6.74167 12.8583 5.275 11.85 3.575 11.1C1.875 10.35 0.0416667 9.94167 -2.08333e-07 9.9C0.0416667 9.85833 1.87083 9.44167 3.5625 8.69167C5.25417 7.94167 6.72083 6.93333 7.9625 5.66667C9.22083 4.4 10.2042 2.91667 10.9125 1.21667C11.6375 -0.5 12 -2.31667 12 -4.23333C12 -2.3 12.3583 -0.483333 13.075 1.21667C13.8083 2.91667 14.7917 4.4 16.025 5.66667C17.275 6.93333 18.75 7.94167 20.45 8.69167C22.15 9.44167 23.9667 9.85833 24 9.9C23.9667 9.94167 22.1417 10.35 20.4375 11.1C18.75 11.85 17.2833 12.8583 16.0375 14.125C14.7917 15.3917 13.8 16.875 13.0625 18.575C12.3417 20.2583 11.9917 22.0667 12 24Z" fill="url(#gemini-gradient)"/>
        <defs>
            <linearGradient id="gemini-gradient" x1="0" y1="12" x2="24" y2="12" gradientUnits="userSpaceOnUse">
                <stop stopColor="#4285F4"/><stop offset="0.5" stopColor="#9B72CB"/><stop offset="1" stopColor="#D96570"/>
            </linearGradient>
        </defs>
    </svg>
);

const AnthropicIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M13.827 3.52h3.603l6.545 16.96h-3.672l-1.303-3.522H13.07l.386-2.627h4.68l-2.452-6.86-1.857 5.027-1.21-3.25V3.52Zm-9.81 0h3.932L13.91 20.48h-3.68l-1.303-3.522H3.706l-1.29 3.522H-1.5L4.016 3.52Zm1.96 11.31h3.56L7.74 9.59l-1.763 5.24Z" fill="#D97706"/>
    </svg>
);

interface ParameterMapping {
    logical_name: string;
    api_parameter: string;
    default_value: number | boolean | string;
    description?: string;
}

interface ModelConfiguration {
    model_name: string;
    display_name: string;
    description: string;
    is_active: boolean;
    api_version: string;
    provider?: 'azure' | 'google' | 'anthropic_vertex'; // Provider field (azure, google, anthropic_vertex)
    sort_order?: number; // Add sort order field
    parameter_mappings: ParameterMapping[];
}

interface ModelManagementProps {
    value: ModelConfiguration[];
    onChange: (models: ModelConfiguration[]) => void;
    disabled?: boolean;
}

const stackTokens: IStackTokens = { childrenGap: 15 };

const styles = mergeStyleSets({
    container: {
        padding: '20px',
        backgroundColor: '#fafafa',
        borderRadius: '8px',
        border: '1px solid #e1e1e1'
    },
    modelCard: {
        padding: '16px',
        margin: '8px 0',
        border: '1px solid #e1e1e1',
        borderRadius: '8px',
        backgroundColor: 'white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        ':hover': {
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
        }
    },
    modelHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px'
    },
    modelName: {
        fontSize: '16px',
        fontWeight: FontWeights.semibold,
        color: '#323130'
    },
    parameterItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 0',
        borderBottom: '1px solid #f0f0f0'
    },
    statusBadge: {
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: FontWeights.semibold
    },
    activeBadge: {
        backgroundColor: '#d4edda',
        color: '#155724'
    },
    inactiveBadge: {
        backgroundColor: '#f8d7da',
        color: '#721c24'
    },
    actionButton: {
        marginLeft: '8px'
    },
    panelContent: {
        padding: '20px'
    }
});

const ModelManagement: React.FC<ModelManagementProps> = ({ value, onChange, disabled }) => {
    const [models, setModels] = useState<ModelConfiguration[]>(value || []);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ text: string; type: MessageBarType } | null>(null);
    const [showEditPanel, setShowEditPanel] = useState(false);
    const [editingModel, setEditingModel] = useState<ModelConfiguration | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [modelToDelete, setModelToDelete] = useState<string | null>(null);
    const [clearingCache, setClearingCache] = useState(false);
    const [validatingModel, setValidatingModel] = useState(false);
    const [validationResult, setValidationResult] = useState<{
        success: boolean;
        error_message?: string;
        recommendations?: string;
        test_response?: string;
        model_info?: any;
    } | null>(null);

    // Default parameter types with descriptions
    // Note: Streaming is always enabled by default and cannot be disabled
    const parameterTypes: IChoiceGroupOption[] = [
        { key: 'max_tokens', text: 'Max Tokens', iconProps: { iconName: 'NumberField' } },
        { key: 'temperature', text: 'Temperature', iconProps: { iconName: 'Temperature' } },
        { key: 'top_p', text: 'Top P', iconProps: { iconName: 'Percentage' } },
        { key: 'top_k', text: 'Top K (RAG only)', iconProps: { iconName: 'NumberField' } },
        { key: 'max_completion_tokens', text: 'Max Completion Tokens', iconProps: { iconName: 'NumberField' } }
    ];

    // Provider options with brand icons
    const providerOptions: IChoiceGroupOption[] = [
        { 
            key: 'azure', 
            text: 'Azure OpenAI',
            onRenderField: (props, render) => (
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    {render!(props)}
                    <AzureIcon size={18} />
                </Stack>
            )
        },
        { 
            key: 'google', 
            text: 'Google Gemini',
            onRenderField: (props, render) => (
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    {render!(props)}
                    <GeminiIcon size={18} />
                </Stack>
            )
        },
        { 
            key: 'anthropic_vertex', 
            text: 'Anthropic Claude (via Vertex AI)',
            onRenderField: (props, render) => (
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    {render!(props)}
                    <AnthropicIcon size={18} />
                </Stack>
            )
        }
    ];

    // Get default parameters based on provider
    const getDefaultParametersForProvider = (provider: 'azure' | 'google' | 'anthropic_vertex'): ParameterMapping[] => {
        if (provider === 'google') {
            return [
                { logical_name: 'temperature', api_parameter: 'temperature', default_value: 0.2 },
                { logical_name: 'top_p', api_parameter: 'top_p', default_value: 0.95 },
                { logical_name: 'top_k', api_parameter: 'top_k', default_value: 40 },
                { logical_name: 'max_tokens', api_parameter: 'max_output_tokens', default_value: 1200 }
            ];
        } else if (provider === 'anthropic_vertex') {
            // Claude via Google Vertex AI
            // Note: Claude doesn't allow both temperature and top_p together - use only temperature
            return [
                { logical_name: 'max_tokens', api_parameter: 'max_tokens', default_value: 4096 },
                { logical_name: 'temperature', api_parameter: 'temperature', default_value: 0.7 }
            ];
        } else {
            // Azure OpenAI - Streaming is always enabled at the API level
            return [
                { logical_name: 'max_tokens', api_parameter: 'max_tokens', default_value: 1000 },
                { logical_name: 'temperature', api_parameter: 'temperature', default_value: 0.7 },
                { logical_name: 'top_p', api_parameter: 'top_p', default_value: 0.95 }
            ];
        }
    };

    useEffect(() => {
        setModels(value || []);
    }, [value]);

    const handleModelChange = (updatedModels: ModelConfiguration[]) => {
        setModels(updatedModels);
        onChange(updatedModels);
    };

    const openEditPanel = (model?: ModelConfiguration) => {
        if (model) {
            setEditingModel({ ...model });
        } else {
            // Create new model with default provider and next sort order
            const nextSortOrder = models.length > 0 ? Math.max(...models.map(m => m.sort_order || 0)) + 1 : 1;
            setEditingModel({
                model_name: '',
                display_name: '',
                description: '',
                is_active: true,
                provider: 'azure', // Default to Azure
                api_version: '2024-05-01-preview',
                sort_order: nextSortOrder,
                parameter_mappings: getDefaultParametersForProvider('azure')
            });
        }
        setValidationResult(null); // Clear previous validation results
        setShowEditPanel(true);
    };

    const closeEditPanel = () => {
        setShowEditPanel(false);
        setEditingModel(null);
        setValidationResult(null); // Clear validation results when closing
    };

    const saveModel = () => {
        if (!editingModel) return;

        const existingIndex = models.findIndex(m => m.model_name === editingModel.model_name);
        let updatedModels: ModelConfiguration[];

        if (existingIndex >= 0) {
            // Update existing
            updatedModels = [...models];
            updatedModels[existingIndex] = editingModel;
        } else {
            // Add new
            updatedModels = [...models, editingModel];
        }

        handleModelChange(updatedModels);
        closeEditPanel();
        setMessage({ text: 'Model configuration saved successfully', type: MessageBarType.success });
    };

    const confirmDeleteModel = (modelName: string) => {
        setModelToDelete(modelName);
        setShowDeleteDialog(true);
    };

    const deleteModel = () => {
        if (!modelToDelete) return;

        const updatedModels = models.filter(m => m.model_name !== modelToDelete);
        handleModelChange(updatedModels);
        setShowDeleteDialog(false);
        setModelToDelete(null);
        setMessage({ text: 'Model configuration deleted successfully', type: MessageBarType.success });
    };

    const toggleModelActive = (modelName: string) => {
        const updatedModels = models.map(m => 
            m.model_name === modelName ? { ...m, is_active: !m.is_active } : m
        );
        handleModelChange(updatedModels);
    };

    const cloneModel = (model: ModelConfiguration) => {
        // Generate a unique name for the cloned model
        const baseName = model.model_name;
        let cloneName = `${baseName}-copy`;
        let counter = 1;
        
        // Check if the name already exists and increment counter
        while (models.some(m => m.model_name === cloneName)) {
            counter++;
            cloneName = `${baseName}-copy-${counter}`;
        }
        
        // Calculate next sort order
        const nextSortOrder = models.length > 0 ? Math.max(...models.map(m => m.sort_order || 0)) + 1 : 1;
        
        // Create the cloned model
        const clonedModel: ModelConfiguration = {
            ...model,
            model_name: cloneName,
            display_name: `${model.display_name} (Copy)`,
            sort_order: nextSortOrder,
            // Deep copy parameter_mappings to avoid reference issues
            parameter_mappings: model.parameter_mappings.map(p => ({ ...p }))
        };
        
        // Open edit panel with the cloned model for further customization
        setEditingModel(clonedModel);
        setValidationResult(null);
        setShowEditPanel(true);
        setMessage({ text: `Cloned "${model.display_name}" - modify the settings and save`, type: MessageBarType.info });
    };

    const moveModelUp = (modelName: string) => {
        const sortedModels = [...models].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        const currentIndex = sortedModels.findIndex(m => m.model_name === modelName);
        
        if (currentIndex > 0) {
            // Swap sort orders with the model above
            const currentModel = sortedModels[currentIndex];
            const modelAbove = sortedModels[currentIndex - 1];
            
            const updatedModels = models.map(m => {
                if (m.model_name === currentModel.model_name) {
                    return { ...m, sort_order: modelAbove.sort_order || 0 };
                } else if (m.model_name === modelAbove.model_name) {
                    return { ...m, sort_order: currentModel.sort_order || 0 };
                }
                return m;
            });
            
            handleModelChange(updatedModels);
        }
    };

    const moveModelDown = (modelName: string) => {
        const sortedModels = [...models].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        const currentIndex = sortedModels.findIndex(m => m.model_name === modelName);
        
        if (currentIndex < sortedModels.length - 1) {
            // Swap sort orders with the model below
            const currentModel = sortedModels[currentIndex];
            const modelBelow = sortedModels[currentIndex + 1];
            
            const updatedModels = models.map(m => {
                if (m.model_name === currentModel.model_name) {
                    return { ...m, sort_order: modelBelow.sort_order || 0 };
                } else if (m.model_name === modelBelow.model_name) {
                    return { ...m, sort_order: currentModel.sort_order || 0 };
                }
                return m;
            });
            
            handleModelChange(updatedModels);
        }
    };

    const handleClearCache = async () => {
        setClearingCache(true);
        try {
            const response = await authenticatedFetch('/api/admin/cache/clear', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                setMessage({ 
                    text: 'Model cache cleared successfully! Model changes will now appear immediately.', 
                    type: MessageBarType.success 
                });
                
                // Refresh the bootstrap data to update model lists in UI
                try {
                    const { getBootstrapData } = await import('../../api/api');
                    const bootstrapData = await getBootstrapData();
                    
                    if (bootstrapData?.models) {
                        // Trigger a custom event to update app state with new models
                        const event = new CustomEvent('modelsUpdated', {
                            detail: {
                                models: bootstrapData.models.models || [],
                                model_options: bootstrapData.models.model_options || [],
                                default_model: bootstrapData.models.default_model
                            }
                        });
                        window.dispatchEvent(event);
                        
                        setMessage({ 
                            text: 'Cache cleared and models refreshed! New models are now available in the UI.', 
                            type: MessageBarType.success 
                        });
                    }
                } catch (refreshError) {
                    console.warn('Failed to refresh models:', refreshError);
                    // Fallback to page reload after a short delay
                    setTimeout(() => {
                        window.location.reload();
                    }, 2000);
                }
            } else {
                const result = await response.json();
                setMessage({ 
                    text: `Failed to clear cache: ${result.message || 'Unknown error'}`, 
                    type: MessageBarType.error 
                });
            }
        } catch (error) {
            setMessage({ 
                text: `Error clearing cache: ${error}`, 
                type: MessageBarType.error 
            });
        } finally {
            setClearingCache(false);
        }
    };

    const validateModel = async () => {
        if (!editingModel) return;
        
        setValidatingModel(true);
        setValidationResult(null);
        
        try {
            const response = await authenticatedFetch('/api/admin/models/validate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(editingModel)
            });
            
            if (response.ok) {
                const result = await response.json();
                setValidationResult(result);
                
                if (result.success) {
                    setMessage({ 
                        text: '✅ Model configuration is valid and working correctly!', 
                        type: MessageBarType.success 
                    });
                } else {
                    setMessage({ 
                        text: `❌ Model validation failed: ${result.error_message}`, 
                        type: MessageBarType.error 
                    });
                }
            } else {
                const errorData = await response.json();
                setMessage({ 
                    text: `Validation request failed: ${errorData.error_message || 'Unknown error'}`, 
                    type: MessageBarType.error 
                });
            }
        } catch (error) {
            setMessage({ 
                text: `Error validating model: ${error}`, 
                type: MessageBarType.error 
            });
        } finally {
            setValidatingModel(false);
        }
    };

    const updateParameterValue = (paramIndex: number, value: any) => {
        if (!editingModel) return;

        const updatedMappings = [...editingModel.parameter_mappings];
        updatedMappings[paramIndex] = { ...updatedMappings[paramIndex], default_value: value };
        setEditingModel({ ...editingModel, parameter_mappings: updatedMappings });
    };

    const addParameter = () => {
        if (!editingModel) return;

        const newParameter: ParameterMapping = {
            logical_name: 'new_parameter',
            api_parameter: 'new_parameter',
            default_value: '' // Default to empty string for text input
        };

        setEditingModel({
            ...editingModel,
            parameter_mappings: [...editingModel.parameter_mappings, newParameter]
        });
    };

    const removeParameter = (index: number) => {
        if (!editingModel) return;

        const updatedMappings = editingModel.parameter_mappings.filter((_, i) => i !== index);
        setEditingModel({ ...editingModel, parameter_mappings: updatedMappings });
    };

    const renderParameterEditor = (param: ParameterMapping, index: number) => {
        const renderValueEditor = () => {
            // Check if this should be a boolean (stream parameter removed - always enabled)
            if (typeof param.default_value === 'boolean') {
                return (
                    <Checkbox
                        checked={Boolean(param.default_value)}
                        onChange={(_, checked) => updateParameterValue(index, checked)}
                    />
                );
            }
            
            // Check if this should be a number (but not if it's currently a string)
            const isNumericParam = (
                typeof param.default_value === 'number' || 
                (param.logical_name.includes('token') && typeof param.default_value !== 'string') ||
                (['temperature', 'top_p', 'top_k', 'frequency_penalty', 'presence_penalty'].includes(param.logical_name) && typeof param.default_value !== 'string')
            );
            
            if (isNumericParam) {
                return (
                    <TextField
                        value={String(param.default_value)}
                        onChange={(_, value) => {
                            // Handle different numeric types
                            let numericValue;
                            if (param.logical_name === 'top_k' || (param.logical_name.includes('token') && !param.logical_name.includes('completion'))) {
                                // Integer values
                                numericValue = value ? parseInt(value) || 0 : 0;
                            } else {
                                // Float values (temperature, top_p)
                                numericValue = value ? parseFloat(value) || 0 : 0;
                            }
                            updateParameterValue(index, numericValue);
                        }}
                        type="number"
                        style={{ width: '150px' }}
                        placeholder={`Enter ${param.logical_name}`}
                        step={param.logical_name === 'top_k' || (param.logical_name.includes('token') && !param.logical_name.includes('completion')) ? '1' : '0.1'}
                        min={param.logical_name === 'top_k' ? '1' : '0'}
                        max={param.logical_name === 'temperature' ? '2' : param.logical_name === 'top_p' ? '1' : param.logical_name === 'top_k' ? '50' : undefined}
                        description={param.logical_name === 'top_k' ? 'Number of documents to retrieve for Azure Search RAG (max 50)' : undefined}
                    />
                );
            } else {
                // Text input for strings and other non-numeric values
                return (
                    <TextField
                        value={String(param.default_value || '')}
                        onChange={(_, value) => updateParameterValue(index, value || '')}
                        type="text"
                        style={{ width: '150px' }}
                        placeholder={`Enter ${param.logical_name} (text)`}
                        description="Text parameter - use for model names, API endpoints, etc."
                    />
                );
            }
        };

        return (
            <Stack key={index} horizontal verticalAlign="center" tokens={{ childrenGap: 10 }} className={styles.parameterItem}>
                <Stack horizontal tokens={{ childrenGap: 10 }} style={{ flex: 1 }}>
                    <TextField
                        value={param.logical_name}
                        onChange={(_, value) => {
                            const updatedMappings = [...editingModel!.parameter_mappings];
                            updatedMappings[index] = { ...updatedMappings[index], logical_name: value || '' };
                            setEditingModel({ ...editingModel!, parameter_mappings: updatedMappings });
                        }}
                        placeholder="Parameter name"
                        style={{ width: '150px' }}
                    />
                    <TextField
                        value={param.api_parameter}
                        onChange={(_, value) => {
                            const updatedMappings = [...editingModel!.parameter_mappings];
                            updatedMappings[index] = { ...updatedMappings[index], api_parameter: value || '' };
                            setEditingModel({ ...editingModel!, parameter_mappings: updatedMappings });
                        }}
                        placeholder="API parameter"
                        style={{ width: '150px' }}
                    />
                    {renderValueEditor()}
                </Stack>
                <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    title="Remove parameter"
                    onClick={() => removeParameter(index)}
                    className={styles.actionButton}
                />
            </Stack>
        );
    };

    return (
        <div className={styles.container}>
            <Stack tokens={stackTokens}>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                    <Stack>
                        <Text variant="xLarge" style={{ fontWeight: FontWeights.semibold }}>
                            Model Management
                        </Text>
                        <Text variant="medium" style={{ color: '#666' }}>
                            Configure AI model parameters and behavior
                        </Text>
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <DefaultButton
                            iconProps={{ iconName: 'ArrowClockwise' }}
                            onClick={handleClearCache}
                            disabled={disabled || clearingCache}
                            title="Clear model cache to make changes appear immediately"
                        >
                            {clearingCache ? 'Clearing...' : 'Clear Cache'}
                        </DefaultButton>
                        <PrimaryButton
                            iconProps={{ iconName: 'Add' }}
                            onClick={() => openEditPanel()}
                            disabled={disabled}
                        >
                            Add Model
                        </PrimaryButton>
                    </Stack>
                </Stack>

                {message && (
                    <MessageBar 
                        messageBarType={message.type}
                        onDismiss={() => setMessage(null)}
                    >
                        {message.text}
                    </MessageBar>
                )}

                {loading && (
                    <Stack horizontalAlign="center" tokens={{ childrenGap: 10 }}>
                        <Spinner size={SpinnerSize.medium} />
                        <Text>Loading models...</Text>
                    </Stack>
                )}

                <Stack tokens={{ childrenGap: 10 }}>
                    {models
                        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                        .map((model, index) => {
                            const sortedModels = models.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
                            const isFirst = index === 0;
                            const isLast = index === sortedModels.length - 1;
                            
                            return (
                        <div key={model.model_name} className={styles.modelCard}>
                            <div className={styles.modelHeader}>
                                <Stack>
                                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                                        {/* Provider-specific brand icons */}
                                        {model.provider === 'google' ? (
                                            <GeminiIcon size={20} />
                                        ) : model.provider === 'anthropic_vertex' ? (
                                            <AnthropicIcon size={20} />
                                        ) : (
                                            <AzureIcon size={20} />
                                        )}
                                        <Text className={styles.modelName}>{model.display_name}</Text>
                                        <div style={{
                                            padding: '2px 8px',
                                            borderRadius: '12px',
                                            fontSize: '11px',
                                            fontWeight: 'semibold',
                                            backgroundColor: model.provider === 'google' ? '#e8f5e8' : model.provider === 'anthropic_vertex' ? '#fef3c7' : '#e8f3ff',
                                            color: model.provider === 'google' ? '#137333' : model.provider === 'anthropic_vertex' ? '#92400e' : '#004578'
                                        }}>
                                            {model.provider === 'google' ? 'Google Gemini' : model.provider === 'anthropic_vertex' ? 'Anthropic Claude' : 'Azure OpenAI'}
                                        </div>
                                        <div className={`${styles.statusBadge} ${model.is_active ? styles.activeBadge : styles.inactiveBadge}`}>
                                            {model.is_active ? 'Active' : 'Inactive'}
                                        </div>
                                        <Text variant="tiny" style={{ color: '#999', marginLeft: 'auto' }}>
                                            Order: {model.sort_order || 0}
                                        </Text>
                                    </Stack>
                                    <Text variant="small" style={{ color: '#666' }}>{model.description}</Text>
                                    <Text variant="small" style={{ color: '#666' }}>
                                        Model: {model.model_name}
                                        {model.api_version && ` | API Version: ${model.api_version}`}
                                        {model.parameter_mappings.find(p => p.logical_name === 'max_tokens') && 
                                            ` | Max Tokens: ${model.parameter_mappings.find(p => p.logical_name === 'max_tokens')?.default_value}`
                                        }
                                    </Text>
                                </Stack>
                                <Stack horizontal tokens={{ childrenGap: 5 }}>
                                    <Stack horizontal tokens={{ childrenGap: 2 }} title="Sort Order">
                                        <IconButton
                                            iconProps={{ iconName: 'ChevronUp' }}
                                            title="Move up"
                                            disabled={isFirst}
                                            onClick={() => moveModelUp(model.model_name)}
                                            className={styles.actionButton}
                                            styles={{
                                                root: { opacity: isFirst ? 0.3 : 1 }
                                            }}
                                        />
                                        <IconButton
                                            iconProps={{ iconName: 'ChevronDown' }}
                                            title="Move down"
                                            disabled={isLast}
                                            onClick={() => moveModelDown(model.model_name)}
                                            className={styles.actionButton}
                                            styles={{
                                                root: { opacity: isLast ? 0.3 : 1 }
                                            }}
                                        />
                                    </Stack>
                                    <Checkbox
                                        checked={model.is_active}
                                        onChange={() => toggleModelActive(model.model_name)}
                                        label="Active"
                                    />
                                    <IconButton
                                        iconProps={{ iconName: 'Copy' }}
                                        title="Clone model configuration"
                                        onClick={() => cloneModel(model)}
                                        className={styles.actionButton}
                                    />
                                    <IconButton
                                        iconProps={{ iconName: 'Edit' }}
                                        title="Edit model"
                                        onClick={() => openEditPanel(model)}
                                        className={styles.actionButton}
                                    />
                                    <IconButton
                                        iconProps={{ iconName: 'Delete' }}
                                        title="Delete model"
                                        onClick={() => confirmDeleteModel(model.model_name)}
                                        className={styles.actionButton}
                                    />
                                </Stack>
                            </div>
                            
                            <Stack tokens={{ childrenGap: 5 }}>
                                <Text variant="small" style={{ fontWeight: FontWeights.semibold }}>Configured Parameters:</Text>
                                <Stack horizontal wrap tokens={{ childrenGap: 15 }}>
                                    {model.parameter_mappings.map((param, idx) => (
                                        <Stack key={idx} style={{ minWidth: '120px' }}>
                                            <Text variant="small" style={{ color: '#666', fontWeight: FontWeights.semibold }}>
                                                {param.logical_name}
                                            </Text>
                                            <Text variant="small" style={{ color: '#333' }}>
                                                {String(param.default_value)}
                                            </Text>
                                            <Text variant="small" style={{ color: '#999', fontSize: '10px' }}>
                                                ({param.api_parameter})
                                            </Text>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Stack>
                        </div>
                    )})}
                </Stack>

                {models.length === 0 && !loading && (
                    <Stack horizontalAlign="center" tokens={{ childrenGap: 10 }} style={{ padding: '40px' }}>
                        <BrainCircuitRegular style={{ fontSize: 48, color: '#ccc' }} />
                        <Text style={{ color: '#666' }}>No models configured</Text>
                        <Text variant="small" style={{ color: '#999' }}>
                            Add a model configuration to get started
                        </Text>
                    </Stack>
                )}
            </Stack>

            {/* Edit Panel */}
            <Panel
                isOpen={showEditPanel}
                onDismiss={closeEditPanel}
                type={PanelType.medium}
                headerText={editingModel?.model_name ? `Edit ${editingModel.display_name}` : 'Add New Model'}
                closeButtonAriaLabel="Close"
            >
                <div className={styles.panelContent}>
                    {editingModel && (
                        <Stack tokens={stackTokens}>
                            <ChoiceGroup
                                label="Model Provider"
                                options={providerOptions}
                                selectedKey={editingModel.provider || 'azure'}
                                onChange={(_, option) => {
                                    const newProvider = option?.key as 'azure' | 'google' | 'anthropic_vertex';
                                    setEditingModel({ 
                                        ...editingModel, 
                                        provider: newProvider,
                                        // Update parameters when provider changes
                                        parameter_mappings: getDefaultParametersForProvider(newProvider),
                                        // Update API version based on provider (not needed for Google or Claude)
                                        api_version: newProvider === 'azure' ? '2024-05-01-preview' : ''
                                    });
                                }}
                                required
                            />
                            
                            <TextField
                                label="Model Name"
                                value={editingModel.model_name}
                                onChange={(_, value) => setEditingModel({ ...editingModel, model_name: value || '' })}
                                required
                                placeholder={editingModel.provider === 'google' ? 'e.g., gemini-2.0-flash' : editingModel.provider === 'anthropic_vertex' ? 'e.g., claude-sonnet-4-20250514' : 'e.g., gpt-4o'}
                                description={editingModel.provider === 'google' ? 'Gemini model name from Google AI Studio or Vertex AI' : editingModel.provider === 'anthropic_vertex' ? 'Claude model name (e.g., claude-opus-4-6, claude-sonnet-4-20250514)' : 'Azure OpenAI deployment name'}
                            />
                            <TextField
                                label="Display Name"
                                value={editingModel.display_name}
                                onChange={(_, value) => setEditingModel({ ...editingModel, display_name: value || '' })}
                                required
                                placeholder={editingModel.provider === 'google' ? 'e.g., Gemini 2.0 Flash' : editingModel.provider === 'anthropic_vertex' ? 'e.g., Claude Opus 4' : 'e.g., GPT-4 Omni'}
                            />
                            <TextField
                                label="Description"
                                value={editingModel.description}
                                onChange={(_, value) => setEditingModel({ ...editingModel, description: value || '' })}
                                multiline
                                rows={3}
                                placeholder="Model description..."
                            />
                            {editingModel.provider === 'azure' && (
                                <TextField
                                    label="API Version"
                                    value={editingModel.api_version}
                                    onChange={(_, value) => setEditingModel({ ...editingModel, api_version: value || '' })}
                                    placeholder="2024-05-01-preview"
                                    description="Azure OpenAI API version (only needed for Azure models)"
                                />
                            )}
                            <Checkbox
                                label="Active"
                                checked={editingModel.is_active}
                                onChange={(_, checked) => setEditingModel({ ...editingModel, is_active: checked || false })}
                            />

                            <TextField
                                label="Sort Order"
                                value={String(editingModel.sort_order || 1)}
                                onChange={(_, value) => {
                                    const numValue = parseInt(value || '1') || 1;
                                    setEditingModel({ ...editingModel, sort_order: numValue });
                                }}
                                type="number"
                                min={1}
                                description="Lower numbers appear first in the model list (1 = top, higher = lower)"
                            />

                            <Separator />
                            
                            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                                <Label>Parameter Mappings</Label>
                                <DefaultButton
                                    iconProps={{ iconName: 'Add' }}
                                    onClick={addParameter}
                                >
                                    Add Parameter
                                </DefaultButton>
                            </Stack>
                            
                            <Stack tokens={{ childrenGap: 10 }}>
                                {editingModel.parameter_mappings.map((param, index) => 
                                    renderParameterEditor(param, index)
                                )}
                            </Stack>

                            <Separator />
                            
                            {/* Model Validation Section */}
                            <Stack tokens={{ childrenGap: 15 }}>
                                <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                                    <Stack tokens={{ childrenGap: 5 }}>
                                        <Label>Model Validation</Label>
                                        <Text variant="small" style={{ color: '#666' }}>
                                            Test this model configuration to ensure it works correctly
                                        </Text>
                                    </Stack>
                                    <DefaultButton
                                        iconProps={{ iconName: validatingModel ? 'ArrowSync' : 'Beaker' }}
                                        onClick={validateModel}
                                        disabled={validatingModel || !editingModel.model_name}
                                        title="Validate model configuration"
                                    >
                                        {validatingModel ? 'Validating...' : 'Validate Model'}
                                    </DefaultButton>
                                </Stack>
                                
                                {/* Validation Results */}
                                {validationResult && (
                                    <Stack tokens={{ childrenGap: 10 }} style={{ 
                                        padding: '15px', 
                                        border: validationResult.success ? '1px solid #107c10' : '1px solid #d83b01',
                                        borderRadius: '4px',
                                        backgroundColor: validationResult.success ? '#f3f9f1' : '#fdf2f2'
                                    }}>
                                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                            {validationResult.success ? (
                                                <CheckmarkCircleRegular style={{ color: '#107c10', fontSize: 20 }} />
                                            ) : (
                                                <WarningRegular style={{ color: '#d83b01', fontSize: 20 }} />
                                            )}
                                            <Text style={{ 
                                                fontWeight: FontWeights.semibold,
                                                color: validationResult.success ? '#107c10' : '#d83b01'
                                            }}>
                                                {validationResult.success ? 'Validation Successful' : 'Validation Failed'}
                                            </Text>
                                        </Stack>
                                        
                                        {validationResult.success && validationResult.test_response && (
                                            <Stack tokens={{ childrenGap: 5 }}>
                                                <Text variant="small" style={{ fontWeight: FontWeights.semibold }}>Model Response:</Text>
                                                <Text variant="small" style={{ 
                                                    fontStyle: 'italic', 
                                                    padding: '8px', 
                                                    backgroundColor: '#fff',
                                                    border: '1px solid #edebe9',
                                                    borderRadius: '3px'
                                                }}>
                                                    {validationResult.test_response}
                                                </Text>
                                            </Stack>
                                        )}
                                        
                                        {!validationResult.success && validationResult.error_message && (
                                            <Stack tokens={{ childrenGap: 5 }}>
                                                <Text variant="small" style={{ fontWeight: FontWeights.semibold }}>Error Details:</Text>
                                                <Text variant="small" style={{ 
                                                    color: '#d83b01',
                                                    padding: '8px', 
                                                    backgroundColor: '#fff',
                                                    border: '1px solid #edebe9',
                                                    borderRadius: '3px',
                                                    fontFamily: 'monospace',
                                                    fontSize: '12px'
                                                }}>
                                                    {validationResult.error_message}
                                                </Text>
                                            </Stack>
                                        )}
                                        
                                        {validationResult.recommendations && (
                                            <Stack tokens={{ childrenGap: 5 }}>
                                                <Text variant="small" style={{ fontWeight: FontWeights.semibold }}>🤖 AI Recommendations:</Text>
                                                <div style={{ 
                                                    padding: '12px', 
                                                    backgroundColor: '#fff',
                                                    border: '1px solid #edebe9',
                                                    borderRadius: '3px',
                                                    whiteSpace: 'pre-wrap'
                                                }}>
                                                    <Text variant="small">{validationResult.recommendations}</Text>
                                                </div>
                                            </Stack>
                                        )}
                                        
                                        {validationResult.model_info && (
                                            <Stack horizontal tokens={{ childrenGap: 15 }}>
                                                <Text variant="small">Provider: <strong>{validationResult.model_info.provider}</strong></Text>
                                                <Text variant="small">Parameters: <strong>{validationResult.model_info.parameters_tested}</strong></Text>
                                            </Stack>
                                        )}
                                    </Stack>
                                )}
                            </Stack>

                            <Stack horizontal tokens={{ childrenGap: 10 }} style={{ marginTop: '20px' }}>
                                <PrimaryButton onClick={saveModel}>
                                    Save Model
                                </PrimaryButton>
                                <DefaultButton onClick={closeEditPanel}>
                                    Cancel
                                </DefaultButton>
                            </Stack>
                        </Stack>
                    )}
                </div>
            </Panel>

            {/* Delete Confirmation Dialog */}
            <Dialog
                hidden={!showDeleteDialog}
                onDismiss={() => setShowDeleteDialog(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Confirm Delete',
                    subText: `Are you sure you want to delete the model configuration "${modelToDelete}"?`
                }}
            >
                <DialogFooter>
                    <PrimaryButton onClick={deleteModel} text="Delete" />
                    <DefaultButton onClick={() => setShowDeleteDialog(false)} text="Cancel" />
                </DialogFooter>
            </Dialog>
        </div>
    );
};

export default ModelManagement;
